import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    OnInit,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Event as RouterEvent, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter, firstValueFrom, take } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { EntityCreatedTableComponent } from '@connect-services/client/components/entity-created-table/entity-created-table.component';
import { AbstractEntityTableView } from '@connect-services/client/components/entity-table/abstract-entity-table-view';
import { EntityTableSelectionComponent } from '@connect-services/client/components/entity-table/entity-table-selection/entity-table-selection.component';
import { EntityTableCellComponent } from '@connect-services/client/components/entity-table-cell/entity-table-cell.component';
import { EntityTableEditToolbarComponent } from '@connect-services/client/components/entity-table-edit-toolbar/entity-table-edit-toolbar.component';
import {
    LabelBadgeComponent,
    LabelBadgeVariant,
    LabelIconPosition,
} from '@connect-services/client/components/label-badge/label-badge.component';
import { SelectAllProgressModalComponent } from '@connect-services/client/components/modals/select-all-progress-modal/select-all-progress-modal.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import {
    Alignment,
    PaginationFooterTemplateComponent,
} from '@connect-services/client/components/pagination-footer-template/pagination-footer-template.component';
import { lastUrlPathValueRegexp } from '@connect-services/client/guards/can-navigate.guard';
import {
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
    TableBroadcastEvent,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { SaveEditModeResultEnum } from '@connect-services/client/models/common.model';
import {
    DepositsService,
    DepositWithFolderAndStandard,
    EditAction,
    FeatureSession,
    LayerAttribute,
    LayerControllerGetEntities200ResponseAllOfItemsInner as DataEntityAdditionalProperties,
    LayerFilterResult,
    LayersService,
    LayerWithAttributes,
    MapFilter,
    PermissionAliasEnum,
} from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { AttributeValidationService } from '@connect-services/client/services/attribute-validation.service';
import { BroadcastChannelService } from '@connect-services/client/services/broadcast-channel.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { EditModeConfirmationService } from '@connect-services/client/services/edit-mode-confirmation.service';
import { EditModeLayerPanelService } from '@connect-services/client/services/edit-mode-layer-panel.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { EntityCreateUiService } from '@connect-services/client/services/entity-create-ui.service';
import { EntityCreatedTableService } from '@connect-services/client/services/entity-created-table.service';
import { EntityEditPanelGuardService } from '@connect-services/client/services/entity-edit-panel-guard.service';
import { FeatureSessionService } from '@connect-services/client/services/feature-session.service';
import { HasPermissionsService } from '@connect-services/client/services/has-permissions.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { LayerEditBadgeService } from '@connect-services/client/services/layer-edit-badge.service';
import {
    SelectedEntitiesByLayerId,
    SelectedEntity,
} from '@connect-services/client/services/map/selection/selection-state.service';
import { EditModeCapabilities, MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapFilterService } from '@connect-services/client/services/map-filter.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { StandardizedLayerService } from '@connect-services/client/services/standardized-layer.service';
import { TableBroadcastService } from '@connect-services/client/services/table-broadcast.service';
import { TableEditModeService } from '@connect-services/client/services/table-edit-mode.service';
import { EN_DATE_FORMAT, TranslateService } from '@connect-services/client/services/translate.service';
import { ValidatorsService } from '@connect-services/client/services/validators.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { TabComponent, TabWithState } from '@connect-services/src/lib/components/tab/tab.component';
import { CustomColumn, CustomTableComponent } from '@connect-services/src/lib/components/table/table.component';
import { InformationSchemaTableExtended } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { TabContentDirective } from '@shared/client/directives/tab-content.directive';

enum DeleteAvailabilityReason {
    AVAILABLE = 'available',
    INSUFFICIENT_PERMISSIONS = 'insufficientPermissions',
    NO_ENTITY_SELECTED = 'noEntitySelected',
    STANDARDIZED_LAYER = 'standardizedLayer',
}

const deleteTooltipKeyByReason: Record<DeleteAvailabilityReason, string> = {
    [DeleteAvailabilityReason.AVAILABLE]: 'editMode.deleteSelection',
    [DeleteAvailabilityReason.INSUFFICIENT_PERMISSIONS]: 'insufficientPermissions',
    [DeleteAvailabilityReason.NO_ENTITY_SELECTED]: 'editMode.noEntitySelected',
    [DeleteAvailabilityReason.STANDARDIZED_LAYER]: 'standardizedDataActionUnavailable',
};

export enum RowClickEditBehavior {
    ENTER_TABLE_EDIT = 'enter_table_edit',
    OPEN_EDITABLE_DETAIL = 'open_editable_detail',
    OPEN_READ_ONLY_DETAIL = 'open_read_only_detail',
}

export enum TableEditMode {
    ENTITY_DETAIL = 'entity-detail',
    NONE = 'none',
    TABLE_EDIT = 'table-edit',
}

type PanelContext = { depositId: string; entityId: string } | null;

type ValidateEditContext = {
    panelContext: PanelContext;
};

export enum CursorType {
    DEFAULT = 'default',
    NOT_ALLOWED = 'not-allowed',
    POINTER = 'pointer',
}

export const baseColumns: CustomColumn[] = [
    {
        key: 'deposit_id',
        label: 'dataName',
        sortable: true,
    },
    {
        key: 'businessIdentifier',
        label: 'businessIdentifier',
        sortable: true,
    },
];

enum TooltipStatusEnum {
    INSUFFICIENT_PERMISSIONS = 'insufficientPermissions',
    NO_ENTITY_CREATED = 'editMode.noEntityCreated',
    VIEW_CREATED_ENTITIES = 'viewCreatedEntities',
}

export const SELECT_ALL_PROGRESS_BAR_MINIMUM_ENTITY_COUNT = 100;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        CheckboxComponent,
        CustomTableComponent,
        EntityCreatedTableComponent,
        EntityTableCellComponent,
        EntityTableEditToolbarComponent,
        EntityTableSelectionComponent,
        LabelBadgeComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        PaginationFooterTemplateComponent,
        RessourcesModule,
        TabComponent,
        TabContentDirective,
    ],
    selector: 'app-entity-table',
    styleUrl: './entity-table.component.scss',
    templateUrl: './entity-table.component.html',
})
export class EntityTableComponent extends AbstractEntityTableView implements OnInit {
    public readonly editModeCapabilities = input<EditModeCapabilities>();
    public override readonly onlySelected = signal<boolean>(false);
    public tableEditToggleValue = false;

    protected readonly Alignment = Alignment;
    protected readonly ButtonType = ButtonType;
    protected readonly canDeleteEntities = computed<boolean>(
        () => this.deleteAvailabilityReason() === DeleteAvailabilityReason.AVAILABLE,
    );
    protected readonly canDisplayTableEditToggle = computed<boolean>(() => {
        if (!this.canUpdate()) {
            return false;
        }

        return this.isTableEditToggleActive();
    });
    protected readonly canHandleRowClick = computed<boolean>(() => {
        const capabilities = this.editModeCapabilities();
        if (!capabilities) {
            return false;
        }

        if (!this.isTableEditToggleActive()) {
            return true;
        }

        return this.tableEditModeService.getRowClickBehavior(capabilities) !== RowClickEditBehavior.ENTER_TABLE_EDIT;
    });
    protected readonly canUseGlobalEditButton = computed<boolean>(() => {
        const capabilities = this.editModeCapabilities();

        if (!capabilities) {
            return false;
        }

        return capabilities.enabled;
    });
    protected readonly canValidateEdits = computed<boolean>(() => {
        const isEditModeActive = this.isTableEditToggleActive();
        if (!isEditModeActive) {
            return false;
        }

        return this.hasPendingEditsGlobal();
    });
    protected readonly columnNamesWithReference = signal<string[]>([]);
    protected readonly currentTableEditMode = computed<TableEditMode>(() => this.tableEditModeService.currentMode());
    protected dateFormatPlaceholder: string = EN_DATE_FORMAT;
    protected readonly deletedEntityIdsForActiveLayer = computed<Set<string>>(() => {
        this.editModeStateService.getStateTick();

        if (!this.isEditSessionActive()) {
            return new Set<string>();
        }

        const projectId = this.projectBloc.value()?.id;
        const layerId = this.activeLayerId();

        if (!projectId || !layerId) {
            return new Set<string>();
        }

        const layerState = this.editModeStateService.getLayerState(projectId, layerId);

        return new Set<string>(
            layerState
                .filter((entityState) => entityState.action === EditAction.DELETE)
                .map((entityState) => entityState.entityId),
        );
    });
    protected readonly deleteTooltip = computed<string>(() =>
        this.translocoService.translate(deleteTooltipKeyByReason[this.deleteAvailabilityReason()]),
    );
    protected readonly entitiesLoaded = signal<number>(0);
    protected readonly hasPendingEditsGlobal = computed<boolean>(() => {
        this.editModeStateService.getStateTick();

        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            return false;
        }

        return !!this.editModeStateService.getPendingEditCount(projectId);
    });
    protected readonly headerCheckboxState = computed<CheckboxState>(() => {
        const activeLayers: LayerWithAttributes[] = this.projectEntityTableService.activeLayers();
        const totalItems: number = this.projectEntityTableService.paginationParameters()?.totalItems ?? 0;

        if (!this.selectionService.isSelectionActive() || !activeLayers?.length || !totalItems) {
            return CheckboxState.UNCHECKED;
        }

        return this.getHeaderCheckboxState(activeLayers[0].id, totalItems);
    });
    protected readonly isActiveLayerEditable = computed<boolean>(() => {
        const activeLayerId = this.activeLayerId();
        if (!activeLayerId || !this.projectEditModeService.editableLayerIds().includes(activeLayerId)) {
            return false;
        }

        return !this.standardizedLayerIds().has(activeLayerId);
    });
    protected readonly isEditSessionActive = computed<boolean>(() => {
        if (this.isExternalPage()) {
            return this.isExternalTableEditActive();
        }

        return this.isProjectEditSessionActive();
    });
    protected readonly isExternalTableEditActive = computed<boolean>(() =>
        this.tableEditModeService.isExternalTableEditActive(),
    );
    protected readonly isInCreationState: boolean = false;
    protected readonly isInlineTableEditActive = computed<boolean>(() => {
        if (
            !this.canUpdate() ||
            !this.isActiveLayerEditable() ||
            this.currentTableEditMode() !== TableEditMode.TABLE_EDIT
        ) {
            return false;
        }

        if (this.homeService.isExternalPage()) {
            return this.isExternalTableEditActive();
        }

        return this.isTableEditToggleActive();
    });
    protected readonly isLoading = signal<boolean>(false);
    protected readonly isProgressBarNeeded = computed<boolean>(
        () => this.layerTotalItems() >= SELECT_ALL_PROGRESS_BAR_MINIMUM_ENTITY_COUNT,
    );
    protected readonly isProjectEditSessionActive = computed<boolean>(() => {
        return this.projectEditModeService.isEditModeActive();
    });
    protected override readonly isSelectableCheckbox = computed<boolean>(() => {
        const activeLayers: LayerWithAttributes[] = this.projectEntityTableService.activeLayers();
        const entities = this.projectEntityTableService.entities();

        return !!activeLayers?.length && !!entities?.length && this.selectionService.isSelectionActive();
    });
    protected readonly isSwitchDisabled = computed<boolean>(() => {
        return !this.entityCreatedTableService.createdEntitiesByLayer().length || !this.hasManagePermission();
    });
    protected readonly isTableCreatedEntities = signal<boolean>(false);
    protected readonly isTableEditSessionActive = computed<boolean>(() => this.tableEditModeService.isEditModeActive());
    protected readonly isTableEditToggleActive = computed<boolean>(() => {
        if (this.isExternalPage()) {
            return this.isExternalTableEditActive();
        }

        return this.isTableEditSessionActive();
    });
    protected readonly layersEditBadges = computed<boolean[]>(() => {
        this.editModeStateService.getStateTick();

        if (!this.projectEntityTableService.layerNames().length) {
            return [];
        }

        if (!this.projectBloc.value() || !this.isTableEditToggleActive()) {
            return this.projectEntityTableService.layerNames().map(() => false);
        }

        return this.layerEditBadgeService.badgesForLayerNames(this.projectEntityTableService.layerNames());
    });
    protected readonly layerTotalItems = signal<number>(0);
    protected readonly selectedEntityCount = computed<number | undefined>(() => {
        if (!this.selectionService.isSelectionActive()) {
            return undefined;
        }

        return this.selectionStateService.selectedEntitiesCount();
    });

    protected readonly selectedLayerCount = computed<number | undefined>(() => {
        if (!this.selectionService.isSelectionActive()) {
            return undefined;
        }

        return this.selectionStateService.selectedLayerCount();
    });
    protected readonly switchTooltip = computed<string | undefined>(() => {
        if (!this.hasManagePermission()) {
            return TooltipStatusEnum.INSUFFICIENT_PERMISSIONS;
        }

        if (!this.entityCreatedTableService.createdEntitiesByLayer().length) {
            return TooltipStatusEnum.NO_ENTITY_CREATED;
        }

        return TooltipStatusEnum.VIEW_CREATED_ENTITIES;
    });
    protected readonly tableColumns = computed<CustomColumn[]>(() => {
        if (!this.projectEntityTableService.activeLayers()?.length) {
            return [];
        }

        const bookmarkedAttributesColumns: CustomColumn[] = this.projectEntityTableService
            .activeLayers()[0]
            .dataAttributes.filter((attribute: LayerAttribute) => attribute.isBookmarked)
            .map((attribute: LayerAttribute) => {
                const column = this.projectEntityTableService.columnsByName()?.get(attribute.column);

                return {
                    key: attribute.column,
                    label: column?.alias ?? attribute.column,
                    sortable: true,
                    tooltip: column?.alias ? column.name : undefined,
                    translate: false,
                };
            });

        return [...baseColumns, ...bookmarkedAttributesColumns];
    });
    protected readonly tabs = computed<TabWithState[]>(() => {
        const filteredLayersName = this.getSelectedLayerFilterNames();

        return this.projectEntityTableService
            .layerNames()
            .map((layerName: string): TabWithState => {
                const layer = this.projectEntityTableService.getLayerByName(layerName) ?? null;

                const createState = this.entityCreateUiService.getCreateState({
                    capabilities: this.editModeCapabilities(),
                    isEditModeActive: this.isTableEditToggleActive(),
                    layer,
                });

                return {
                    active: this.isLayerActive(layerName),
                    canCreate: createState.visible,
                    createDisabled: createState.disabled,
                    createTooltipKey: createState.tooltipKey,
                    disabled:
                        this.isTableEditToggleActive() &&
                        this.currentTableEditMode() === TableEditMode.TABLE_EDIT &&
                        !this.projectEditModeService.editableLayerIds().includes(layer?.id ?? ''),
                    disabledTooltipKey: 'editMode.layerStandardNotEditable',
                    name: layerName,
                };
            })
            .sort((a: TabWithState, b: TabWithState) => {
                const aPriority = this.getTabPriority(a, filteredLayersName);
                const bPriority = this.getTabPriority(b, filteredLayersName);

                return aPriority - bPriority;
            })
            .filter(
                (tab: TabWithState) =>
                    !this.onlySelected() || this.projectEntityTableService.hasSelectedEntitiesForLayerName(tab.name),
            );
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly editModeStateService = inject(EditModeStateService);
    protected readonly entityEditPanelGuardService = inject(EntityEditPanelGuardService);
    protected readonly homeService = inject(HomeService);
    protected readonly LabelBadgeVariant = LabelBadgeVariant;
    protected readonly LabelIconPosition = LabelIconPosition;
    protected readonly projectEditModeService = inject(ProjectEditModeService);
    protected readonly TableEditMode = TableEditMode;

    private readonly broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
    private readonly canUpdate = computed<boolean>(() => {
        const capabilities = this.editModeCapabilities();

        if (!capabilities) {
            return false;
        }

        return capabilities.enabled && capabilities.canUpdate;
    });
    private readonly deleteAvailabilityReason = computed<DeleteAvailabilityReason>(() => {
        const capabilities = this.editModeCapabilities();
        const hasSelectedEntities = this.selectionStateService.hasSelectedEntities();
        const hasStandardizedSelectedLayer = this.hasStandardizedSelectedLayer();

        return this.getDeleteAvailabilityReason(capabilities, hasSelectedEntities, hasStandardizedSelectedLayer);
    });
    private depositById: Record<string, DepositWithFolderAndStandard> = {};
    private readonly featureSessionId = signal<string | undefined>(undefined);
    private readonly hasManagePermission = computed<boolean>(() => {
        if (!this.hasPermissionsService.initialized()) {
            return false;
        }

        return this.hasPermissionsService.checkPermissions([PermissionAliasEnum.MANAGE_EDIT_MODE_CREATE]);
    });
    private readonly hasStandardizedSelectedLayer = computed<boolean>(() => {
        const selectedByLayerId: SelectedEntitiesByLayerId = this.selectionStateService.selectedEntitiesByLayerId();
        const standardizedLayerIds: Set<string> = this.standardizedLayerIds();

        return this.isAnySelectedLayerStandardized(selectedByLayerId, standardizedLayerIds);
    });
    private readonly isExternalPage = computed<boolean>(() => this.homeService.isExternalPage());
    private readonly layerIdsByIndex = signal<Map<number, string>>(new Map());
    private readonly shouldDisplayOnlySelectedEntities = computed<boolean>(
        () => this.selectionService.isSelectionActive() && this.selectionStateService.hasSelectedEntities(),
    );
    private readonly standardizedLayerIds = signal<Set<string>>(new Set<string>());

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly attributeValidationService = inject(AttributeValidationService);
    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly depositsService = inject(DepositsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly editEntityDetailService = inject(EditEntityDetailService);
    private readonly editModeConfirmationService = inject(EditModeConfirmationService);
    private readonly editModeLayerPanelService = inject(EditModeLayerPanelService);
    private readonly entityCreatedTableService = inject(EntityCreatedTableService);
    private readonly entityCreateUiService = inject(EntityCreateUiService);
    private readonly featureSessionService = inject(FeatureSessionService);
    private hasAutoSelectedFilteredTab = false;
    private readonly hasPermissionsService = inject(HasPermissionsService);
    private readonly isTableInitialized = signal(false);
    private readonly layerEditBadgeService = inject(LayerEditBadgeService);
    private readonly layersService = inject(LayersService);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly mapFilterService = inject(MapFilterService);
    private readonly matDialog = inject(MatDialog);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);
    private selectAllProgressDialogRef?: MatDialogRef<SelectAllProgressModalComponent>;
    private readonly standardizedLayerService = inject(StandardizedLayerService);
    private readonly tableBroadcastService = inject(TableBroadcastService);
    private readonly tableEditModeService = inject(TableEditModeService);
    private readonly translateService = inject(TranslateService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        super();

        this.dateFormatPlaceholder = this.translateService.getDateFormat();
        this.projectEntityTableService.currentPanelEntity.set(this.getCurrentEntityId());

        this.router.events
            .pipe(
                filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((): void => {
                if (this.homeService.isExternalPage()) {
                    return;
                }

                this.projectEntityTableService.currentPanelEntity.set(this.getCurrentEntityId());
            });

        this.destroyRef.onDestroy(async () => {
            if (!this.featureSessionId()) {
                return;
            }

            await this.featureSessionService.endSession(this.featureSessionId()!, false);

            this.featureSessionId.set(undefined);
        });

        this.setLayerName();
        this.handleBroadcastChannelEvents();

        this.setOnlySelectedEntities(this.shouldDisplayOnlySelectedEntities());

        effect(async (): Promise<void> => {
            this.onlySelected();

            if (!untracked(() => this.isTableInitialized())) {
                return;
            }

            await untracked(async (): Promise<void> => {
                const currentLayerName = this.projectEntityTableService.activeLayers()?.[0]?.name;

                const currentTabIndex = this.tabs().findIndex((tab: TabWithState) => tab.name === currentLayerName);

                await this.changeTab(currentTabIndex >= 0 ? currentTabIndex : 0);
            });
        });

        effect(async () => {
            if (this.shouldSkipTableInitialization()) {
                return;
            }

            untracked(() => this.isLoading.set(true));
            this.isTableInitialized.set(false);

            this.projectEntityTableService.resetData();
            await this.projectEntityTableService.initializeTableWithData();
            await this.loadStandardizedLayerIds();
            this.isTableInitialized.set(true);

            untracked(() => {
                this.layerTotalItems.set(this.projectEntityTableService.paginationParameters()!.totalItems!);
                this.isLoading.set(false);
            });

            const currentIndex = this.projectEntityTableService.forceTabIndex();
            this.registerCurrentLayerIdForIndex(currentIndex);
        });

        effect(() => {
            const shouldDisplayOnlySelectedEntities = this.shouldDisplayOnlySelectedEntities();

            untracked(() => {
                this.setOnlySelectedEntities(shouldDisplayOnlySelectedEntities);
            });
        });

        effect(() => {
            this.mapFilterService.selectedFilters();
            this.hasAutoSelectedFilteredTab = false;
        });

        effect(async () => {
            await this.applyFilters();
        });

        effect(async () => {
            const layerId = this.activeLayerId();
            if (!layerId) {
                return;
            }

            await this.fetchColumnNamesWithReferences(layerId);
        });

        const channel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);

        channel.postMessage({
            message: BroadcastChannelMessageEnum.TABLE_READY,
            source: BroadcastChannelSourceEnum.TABLE_VIEW,
        });

        channel.close();
    }
    public async changeTab(layerNameIndex: number): Promise<void> {
        await this.projectEntityTableService.changeTab(
            this.tabs().map((tab: TabWithState) => tab.name)[layerNameIndex],
        );

        this.layerTotalItems.set(this.projectEntityTableService.paginationParameters()!.totalItems!);
        this.registerCurrentLayerIdForIndex(layerNameIndex);
        this.projectEntityTableService.forceTabIndex.set(layerNameIndex);

        this.disableTableEditModeForNonEditableLayer();
    }

    public async ngOnInit(): Promise<void> {
        this.featureSessionId.set(
            await this.featureSessionService.startFeatureSession(FeatureSession.FeatureTrackedEnum.TABLE_VIEW, false),
        );

        await this.editModeLayerPanelService.handleEditModePanelChange(false);
    }

    protected async deleteEntities(): Promise<void> {
        await this.mapEditModeService.requestDelete(null, true);
        this.selectionService.resetSelectionState();
    }

    protected getAttributeInformation(columnKey: string): InformationSchemaTableExtended | null {
        return this.attributeInformationContext.getAttributeInformation(columnKey);
    }

    protected getProjectId(): string | undefined {
        return this.projectBloc.value()?.id;
    }

    protected getRowCursor(entityId: string): CursorType {
        if (this.isEntityPendingDeleteInTableMode(entityId) || this.isEntityPendingDeleteInDetailMode(entityId)) {
            return CursorType.NOT_ALLOWED;
        }

        if (!this.canHandleRowClick()) {
            return CursorType.DEFAULT;
        }

        return CursorType.POINTER;
    }

    protected hasNumericType(columnKey: string): boolean {
        if (!this.getAttributeInformation(columnKey)) {
            return false;
        }

        return this.attributeValidationService.isNumericType(this.getAttributeInformation(columnKey));
    }

    protected isCellEditable(entityId: string): boolean {
        return this.isInlineTableEditActive() && !this.isEntityPendingDeleteInTableMode(entityId);
    }

    protected isEntityPendingDeleteInDetailMode(entityId: string): boolean {
        if (!this.isEditSessionActive() || this.currentTableEditMode() !== TableEditMode.ENTITY_DETAIL) {
            return false;
        }

        return this.deletedEntityIdsForActiveLayer().has(entityId);
    }

    protected isEntityPendingDeleteInEditMode(entityId: string): boolean {
        return this.isEntityPendingDeleteInDetailMode(entityId) || this.isEntityPendingDeleteInTableMode(entityId);
    }

    protected isEntityPendingDeleteInTableMode(entityId: string): boolean {
        if (!this.isEditSessionActive() || this.currentTableEditMode() !== TableEditMode.TABLE_EDIT) {
            return false;
        }

        return this.deletedEntityIdsForActiveLayer().has(entityId);
    }

    protected isRowDetailsOpened(entityId: string): boolean {
        if (
            this.isEntityPendingDeleteInEditMode(entityId) ||
            this.currentTableEditMode() === TableEditMode.TABLE_EDIT
        ) {
            return false;
        }

        return this.projectEntityTableService.currentPanelEntity() === entityId;
    }

    protected async onCreateEntity(index: number): Promise<void> {
        const layerName = this.tabs()[index].name;
        const layer = this.projectEntityTableService.getLayerByName(layerName);
        if (!layer || !this.editModeCapabilities()?.canCreate) {
            return;
        }

        await this.projectEntityTableService.openCreateEntityPanel(layer.id, layerName);

        await this.changeTab(index);
    }

    protected async onRowClick(dataEntity: DataEntityAdditionalProperties): Promise<void> {
        if (!this.canHandleRowClick() || this.isEntityPendingDeleteInEditMode(dataEntity.uuid)) {
            return;
        }

        await this.toggleEntityDetailPanel(dataEntity.uuid, dataEntity.deposit_id);
    }

    protected redoEdit(): void {
        void this.editModeStateService.reapplyLastCanceledEdit(this.getProjectId()!);
    }

    protected switchCreatedEntitiesTable(): void {
        const isLeavingCreatedEntities = this.isTableCreatedEntities();
        this.isTableCreatedEntities.update((value: boolean) => !value);

        if (isLeavingCreatedEntities) {
            this.restoreMainTableActiveLayer();
        }
    }

    protected async switchToggledMode(isTableMode: boolean): Promise<void> {
        if (!this.canDisplayTableEditToggle()) {
            return;
        }

        this.tableEditToggleValue = isTableMode;
        const currentEntityId = this.projectEntityTableService.currentPanelEntity() ?? null;

        if (this.homeService.isExternalPage()) {
            this.tableEditModeService.updateModeFromToggleExternalView(isTableMode, currentEntityId);

            if (isTableMode) {
                this.projectEntityTableService.closeEntityDetailPanel();

                return;
            }

            await this.openEntityDetailPanelIfNeededAfterTableMode();

            return;
        }

        this.tableEditModeService.updateModeFromToggle(isTableMode, currentEntityId);

        if (!isTableMode) {
            this.projectEntityTableService.closeEntityDetailPanel();

            return;
        }

        await this.openEntityDetailPanelIfNeededAfterTableMode();
    }

    protected async toggleAllSelected(): Promise<void> {
        const activeLayers: LayerWithAttributes[] = this.projectEntityTableService.activeLayers();

        if (!activeLayers?.length) {
            return;
        }

        const layerId: string = activeLayers[0].id;

        if (this.headerCheckboxState() === CheckboxState.CHECKED) {
            this.unselectAll(layerId);

            return;
        }

        await this.selectAll(layerId);
    }

    protected async toggleEditMode(): Promise<void> {
        if (this.isTableEditSessionActive() && !(await this.entityEditPanelGuardService.confirmPanelLeaveIfNeeded())) {
            return;
        }

        const currentEntityId = this.projectEntityTableService.currentPanelEntity() ?? null;
        const capabilities = this.editModeCapabilities();

        if (!capabilities) {
            return;
        }

        const isActive = await this.tableEditModeService.requestToggleEditMode(capabilities, currentEntityId);

        if (!isActive) {
            const wasDisplayingCreatedEntities = this.isTableCreatedEntities();

            this.isTableCreatedEntities.set(false);

            if (wasDisplayingCreatedEntities) {
                this.restoreMainTableActiveLayer();
            }
        }

        const isExternalTableEditActive = this.isExternalTableEditActive();

        if (isExternalTableEditActive) {
            this.tableEditModeService.leaveTableEditModeExternalView();
            this.tableEditToggleValue = false;
            this.tableBroadcastService.sendRequestSetEditModeToHome(false);

            return;
        }

        const entityId = this.projectEntityTableService.currentPanelEntity() ?? null;
        const canEnterTableEditModeExternalView = this.tableEditModeService.enterTableEditModeExternalView(
            capabilities,
            entityId,
        );

        if (!canEnterTableEditModeExternalView) {
            return;
        }

        this.tableBroadcastService.sendRequestSetEditModeToHome(true);
    }

    protected async toggleEntityDetailPanel(entityId: string, depositId: string): Promise<void> {
        if (!this.canHandleRowClick()) {
            return;
        }

        if (
            this.projectEntityTableService.currentPanelEntity() &&
            !(await this.entityEditPanelGuardService.confirmPanelLeaveIfNeeded())
        ) {
            return;
        }

        const capabilities = this.editModeCapabilities();
        if (!capabilities) {
            return;
        }

        if (this.tableEditModeService.getRowClickBehavior(capabilities) === RowClickEditBehavior.ENTER_TABLE_EDIT) {
            return;
        }

        if (
            this.tableEditModeService.isEditModeActive() &&
            this.tableEditModeService.currentMode() === TableEditMode.ENTITY_DETAIL
        ) {
            await this.editModeLayerPanelService.handleEditModePanelChange(false);
        }

        if (this.tableEditModeService.getRowClickBehavior(capabilities) === RowClickEditBehavior.OPEN_EDITABLE_DETAIL) {
            this.tableEditModeService.enterEntityDetailEditMode(entityId);
            await this.projectEntityTableService.toggleEntityDetailPanel(depositId, entityId);

            return;
        }

        this.tableEditModeService.exitEntityDetailEditMode();
        await this.projectEntityTableService.toggleEntityDetailPanel(depositId, entityId);
    }

    protected toggleOnlySelectedEntities(): void {
        this.setOnlySelectedEntities(!this.onlySelected());
    }

    protected undoEdit(): void {
        void this.editModeStateService.cancelLastEdit(this.getProjectId()!);
    }

    protected validateEdit(): void {
        const editContext = this.getValidateEditContext();
        if (!editContext) {
            return;
        }

        const dialogRef = this.editModeConfirmationService.openSaveChangesModal({
            canEndEditSession: true,
            onSaved: async () => {
                await this.projectEntityTableService.fetchEntities();

                if (!editContext.panelContext?.depositId || !editContext.panelContext?.entityId) {
                    return;
                }

                await this.projectEntityTableService.toggleEntityDetailPanel(
                    editContext.panelContext.depositId,
                    editContext.panelContext.entityId,
                );
            },
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(async (result: SaveEditModeResultEnum | undefined) => {
                if (result !== SaveEditModeResultEnum.SAVED_QUIT) {
                    return;
                }

                await this.handleSavedQuitAfterValidateEdit();
            });
    }

    private async applyFilters(): Promise<void> {
        if (!this.isTableInitialized()) {
            return;
        }

        if (!this.mapFilterService.selectedFilters().length && !this.mapFilterService.previewResultFilters()) {
            await untracked(async () => {
                this.mapFilterService.clearFilters();
                this.hasAutoSelectedFilteredTab = false;
                await this.resetToDefaultTabWithoutFilters();
            });

            return;
        }

        await untracked(async () => {
            await this.mapFilterService.updateAppliedFilters();
            await this.syncInitialTabWithSelectedLayerFilters();
            await this.projectEntityTableService.fetchEntities();
        });
    }

    private disableTableEditModeForNonEditableLayer(): void {
        if (this.currentTableEditMode() !== TableEditMode.TABLE_EDIT || this.isActiveLayerEditable()) {
            return;
        }

        const currentEntityId = this.projectEntityTableService.currentPanelEntity() ?? null;

        this.tableEditToggleValue = false;

        if (this.homeService.isExternalPage()) {
            this.tableEditModeService.updateModeFromToggleExternalView(false, currentEntityId);

            return;
        }

        this.tableEditModeService.updateModeFromToggle(false, currentEntityId);
    }

    private async fetchColumnNamesWithReferences(layerId: string): Promise<void> {
        try {
            const layer = await firstValueFrom(this.layersService.layerControllerGetOne({ id: layerId }));

            if (!layer.deposit || !layer.tableName) {
                this.columnNamesWithReference.set([]);

                return;
            }

            if (!this.depositById[layer.deposit]) {
                this.depositById[layer.deposit] = await firstValueFrom(
                    this.depositsService.depositControllerGetDepositWithFolderAndStandard({
                        id: layer.deposit,
                    }),
                );
            }

            this.columnNamesWithReference.set(
                await this.editEntityDetailService.getColumnNamesWithReferences(
                    this.depositById[layer.deposit],
                    layer.tableName,
                ),
            );
        } catch {
            this.columnNamesWithReference.set([]);
        }
    }

    private getCurrentEntityId(): string | null {
        const match = this.router.url.match(lastUrlPathValueRegexp);
        const entityId = match ? match[0] : null;

        if (!entityId || !ValidatorsService.isValidUuidV4(entityId)) {
            return null;
        }

        return entityId;
    }

    private getDeleteAvailabilityReason(
        capabilities: EditModeCapabilities | undefined,
        hasSelectedEntities: boolean,
        hasStandardizedSelectedLayer: boolean,
    ): DeleteAvailabilityReason {
        if (!capabilities?.enabled || !capabilities.canDelete) {
            return DeleteAvailabilityReason.INSUFFICIENT_PERMISSIONS;
        }

        if (!hasSelectedEntities) {
            return DeleteAvailabilityReason.NO_ENTITY_SELECTED;
        }

        if (hasStandardizedSelectedLayer) {
            return DeleteAvailabilityReason.STANDARDIZED_LAYER;
        }

        return DeleteAvailabilityReason.AVAILABLE;
    }

    private getSelectedLayerFilterNames(): string[] {
        return this.mapFilterService
            .filters()
            .filter((filter: MapFilter): boolean => this.isSelectedWithLayerCondition(filter))
            .flatMap((filter: MapFilter): string[] => filter.conditions.layers!);
    }

    private getTabPriority(tab: TabWithState, filteredLayersName: string[]): number {
        if (!tab.active) {
            return 2;
        }

        if (filteredLayersName.includes(tab.name)) {
            return 0;
        }

        return 1;
    }

    private getValidateEditContext(): ValidateEditContext | null {
        if (!this.editModeCapabilities()?.enabled || !this.isTableEditToggleActive()) {
            return null;
        }

        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            return null;
        }

        if (!this.editModeStateService.getPendingEditCount(projectId)) {
            return null;
        }

        return {
            panelContext: this.projectEntityTableService.currentPanelContext(),
        };
    }

    private handleBeforeUnload = (): void => {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        this.broadcastChannel.close();
    };

    private handleBroadcastChannelEvents(): void {
        this.broadcastChannel.onmessage = async (event: MessageEvent<TableBroadcastEvent>): Promise<void> => {
            const data = event.data;
            AssertService.assertBroadcastChannelEvent(data);

            if (!data.message) {
                return;
            }

            switch (data.message) {
                case BroadcastChannelMessageEnum.REQUEST_LAYER_WINDOW: {
                    const layerName = data.additionalData;
                    if (!layerName) {
                        return;
                    }
                    AssertService.isString(layerName);

                    this.handleRequestLayerWindow(layerName);

                    return;
                }

                case BroadcastChannelMessageEnum.OPEN_CREATE_ENTITY: {
                    const payload = data.additionalData;
                    if (!payload || !this.editModeCapabilities()?.canCreate) {
                        return;
                    }
                    AssertService.assertOpenCreateEntityPayload(payload);

                    await this.projectEntityTableService.openCreateEntityPanel(payload.layerId, payload.layerName);

                    return;
                }

                case BroadcastChannelMessageEnum.CLOSE: {
                    this.handleCloseRequest(data.source);

                    return;
                }
            }
        };
    }

    private handleCloseRequest(source: BroadcastChannelSourceEnum): void {
        if (source !== BroadcastChannelSourceEnum.HOMEPAGE) {
            return;
        }

        this.handleBeforeUnload();
        window.close();
    }

    private handleRequestLayerWindow(layerName: string): void {
        if (layerName !== this.broadcastChannelService.currentLayer()) {
            return;
        }

        this.broadcastChannel.postMessage({
            additionalData: layerName,
            message: BroadcastChannelMessageEnum.LAYER_WINDOW_EXISTS,
            source: BroadcastChannelSourceEnum.TABLE_VIEW,
        });
    }

    private async handleSavedQuitAfterValidateEdit(): Promise<void> {
        this.projectEntityTableService.closeEntityDetailPanel();
        this.tableEditModeService.exitEntityDetailEditMode();

        this.tableEditToggleValue = false;

        if (this.homeService.isExternalPage()) {
            this.tableEditModeService.leaveTableEditModeExternalView();
            this.tableBroadcastService.sendRequestSetEditModeToHome(false);

            return;
        }

        await this.tableEditModeService.ensureCanLeaveTableEditMode();
        this.projectEditModeService.deactivateEditMode();
    }

    private isAnySelectedLayerStandardized(
        selectedByLayerId: Record<string, SelectedEntity[]>,
        standardizedLayerIds: Set<string>,
    ): boolean {
        const selectedLayerIds: string[] = Object.entries(selectedByLayerId)
            .filter(([, entities]: [string, SelectedEntity[]]): number => entities.length)
            .map(([layerId]: [string, SelectedEntity[]]): string => layerId);

        if (!selectedLayerIds.length || !standardizedLayerIds.size) {
            return false;
        }

        return selectedLayerIds.some((layerId: string) => standardizedLayerIds.has(layerId));
    }

    private isLayerActive(layerName: string): boolean {
        const layers: LayerWithAttributes[] = this.projectEntityTableService.layersByName()!.get(layerName) ?? [];

        if (!layers.length) {
            return true;
        }

        const layerIds: string[] = layers.map((layer) => layer.id);
        const layerFiltered: LayerFilterResult[] | undefined = this.mapFilterService
            .appliedFiltersResult()
            .filter((filterResult: LayerFilterResult) => layerIds.includes(filterResult.layerId));

        if (!layerFiltered.length) {
            return true;
        }

        const allEntityIds: string[] = layerFiltered.flatMap((layer: LayerFilterResult): string[] => layer.entityIds);

        return !!allEntityIds.length;
    }

    private isSelectedWithLayerCondition(filter: MapFilter): boolean {
        return this.mapFilterService.selectedFilters().includes(filter.id) && !!filter.conditions.layers;
    }

    private async loadStandardizedLayerIds(): Promise<void> {
        const layers = this.projectEntityTableService
            .layerNames()
            .flatMap((layerName: string): LayerWithAttributes[] => {
                return this.projectEntityTableService.layersByName()?.get(layerName) ?? [];
            });

        this.standardizedLayerIds.set(await this.standardizedLayerService.getStandardizedLayerIds(layers));
    }

    private async openEntityDetailPanelIfNeededAfterTableMode(): Promise<void> {
        const panelContext = this.projectEntityTableService.currentPanelContext();

        if (!panelContext?.depositId || !panelContext?.entityId) {
            this.projectEntityTableService.closeEntityDetailPanel();

            return;
        }

        await this.projectEntityTableService.toggleEntityDetailPanel(panelContext.depositId, panelContext.entityId);
    }

    private registerCurrentLayerIdForIndex(index: number): void {
        const activeLayers = this.projectEntityTableService.activeLayers();

        if (!activeLayers?.length) {
            return;
        }

        const currentMap = new Map(this.layerIdsByIndex());
        currentMap.set(index, activeLayers[0].id);
        this.layerIdsByIndex.set(currentMap);
    }

    private async resetToDefaultTabWithoutFilters(): Promise<void> {
        const defaultLayerName: string = this.onlySelected()
            ? this.tabs()[0]?.name
            : this.projectEntityTableService.layerNames()[0];

        if (!defaultLayerName) {
            return;
        }

        await this.projectEntityTableService.changeTab(defaultLayerName);

        const defaultTabIndex = this.tabs().findIndex((tab: TabWithState) => tab.name === defaultLayerName);

        if (defaultTabIndex < 0) {
            return;
        }

        this.projectEntityTableService.forceTabIndex.set(
            this.projectEntityTableService.layerNames().indexOf(defaultLayerName),
        );

        this.registerCurrentLayerIdForIndex(defaultTabIndex);
    }

    private restoreMainTableActiveLayer(): void {
        const tabIndex = this.projectEntityTableService.forceTabIndex();
        const layerId = this.layerIdsByIndex().get(tabIndex);

        if (!layerId) {
            return;
        }

        const layer = this.projectEntityTableService
            .layers()
            .find((currentLayer: LayerWithAttributes) => currentLayer.id === layerId);

        if (!layer) {
            return;
        }

        this.projectEntityTableService.setActiveLayerByName(layer.name);
    }

    private async selectAll(layerId: string): Promise<void> {
        this.entitiesLoaded.set(0);

        if (this.isProgressBarNeeded()) {
            this.selectAllProgressDialogRef = this.matDialog.open(SelectAllProgressModalComponent, {
                data: {
                    total: this.layerTotalItems(),
                },
                disableClose: true,
                width: '40vw',
            });
        }

        await this.selectAllEntitiesForLayer(layerId, this.isProgressBarNeeded());

        this.projectEntityTableService.unSelectedEntitiesAfterFilterByLayerId.update(
            (unSelectedEntitiesByLayerId: SelectedEntitiesByLayerId): SelectedEntitiesByLayerId =>
                Object.fromEntries(
                    Object.entries(unSelectedEntitiesByLayerId).filter(
                        ([key]: [string, SelectedEntity[]]) => key !== layerId,
                    ),
                ),
        );
    }

    private async selectAllEntitiesForLayer(layerId: string, showProgressBar: boolean): Promise<void> {
        try {
            const allEntities: SelectedEntity[] = await this.selectionService.fetchSelectedEntitiesForLayer(
                layerId,
                { filterIds: this.mapFilterService.selectedFilters() },
                (delta: number) => {
                    this.entitiesLoaded.update((count: number) => count + delta);

                    if (showProgressBar) {
                        this.selectAllProgressDialogRef?.componentInstance.entitiesLoaded.set(this.entitiesLoaded());
                    }
                },
            );
            const isLayerVisible: boolean = this.mapLayerService.isLayerCurrentlyVisible(layerId);

            this.selectionStateService.updateSelectedEntitiesByLayerId((previous: SelectedEntitiesByLayerId) => {
                return {
                    ...previous,
                    [layerId]: allEntities.map((entity: SelectedEntity) => {
                        return {
                            ...entity,
                            isVisible: isLayerVisible,
                            uuid: entity.uuid,
                        };
                    }),
                };
            });
        } finally {
            this.selectAllProgressDialogRef?.close();
            this.selectAllProgressDialogRef = undefined;
        }
    }

    private setLayerName(): void {
        const layerName = this.activatedRoute.snapshot.queryParamMap.get('layerName');
        if (!layerName) {
            return;
        }

        const decoded = decodeURI(layerName);

        this.broadcastChannelService.currentLayer.set(decoded);
        this.projectEntityTableService.layerNames.set([decoded]);

        if (this.homeService.isExternalPage()) {
            window.addEventListener('beforeunload', this.handleBeforeUnload);
            this.destroyRef.onDestroy(() => {
                this.handleBeforeUnload();
            });
        }
    }

    private setOnlySelectedEntities(onlySelected: boolean): void {
        this.onlySelected.set(onlySelected);
        this.projectEntityTableService.isOnlySelectedEntitiesTabs.set(onlySelected);
        this.projectEntityTableService.unSelectedEntitiesAfterFilterByLayerId.set({});
    }

    private shouldSkipTableInitialization(): boolean {
        return !this.projectBloc.value();
    }

    private async syncInitialTabWithSelectedLayerFilters(): Promise<void> {
        if (this.hasAutoSelectedFilteredTab) {
            return;
        }

        const filteredLayerNames = this.getSelectedLayerFilterNames().toSorted();

        if (!filteredLayerNames.length) {
            return;
        }

        const currentActiveLayerName = this.projectEntityTableService.activeLayers()?.[0]?.name;
        if (currentActiveLayerName && filteredLayerNames.includes(currentActiveLayerName)) {
            this.hasAutoSelectedFilteredTab = true;

            return;
        }

        const nextLayerName = filteredLayerNames.find((layerName: string) =>
            this.projectEntityTableService.layerNames().includes(layerName),
        );

        if (!nextLayerName) {
            return;
        }

        await this.projectEntityTableService.changeTab(nextLayerName);

        const syncedTabIndex = this.tabs().findIndex((tab: TabWithState) => tab.name === nextLayerName);
        if (syncedTabIndex >= 0) {
            this.registerCurrentLayerIdForIndex(syncedTabIndex);
            this.projectEntityTableService.forceTabIndex.set(syncedTabIndex);
        }

        this.hasAutoSelectedFilteredTab = true;
    }

    private unselectAll(layerId: string): void {
        this.projectEntityTableService.unSelectedEntitiesAfterFilterByLayerId.update((previous) => {
            return {
                ...previous,
                [layerId]: this.selectionStateService.selectedEntitiesByLayerId()[layerId] ?? [],
            };
        });

        this.selectionStateService.updateSelectedEntitiesByLayerId((previous: SelectedEntitiesByLayerId) => {
            return Object.fromEntries(
                Object.entries(previous).filter(([key]: [string, SelectedEntity[]]) => key !== layerId),
            );
        });
    }
}
